import type { LeafPrimitive, Zone, ZoneComponent } from '@tsagko/label-contracts'
import { ratioForChildSize } from '../block-configurator/dimension-ops'

// The shape splitZone plants in a freshly-split child (see placeholderText
// below). A text component whose content is the empty string is structurally
// present but visually empty — for merge-confirmation purposes it must be
// treated as "no content," not "user content."
function isEmptyPlaceholder(component: ZoneComponent): boolean {
  return component.type === 'text' && component.value.content === ''
}

export type SplitDirection = 'row' | 'column'
export type IdGen = () => string

let randomIdCounter = 0
const defaultIdGen: IdGen = () => `z-${++randomIdCounter}-${Date.now().toString(36)}`

function placeholderText(idGen: IdGen): LeafPrimitive {
  return {
    type: 'text',
    id: idGen(),
    value: { content: '' },
  }
}

export type FillerFactory = (idGen: IdGen) => Zone

const defaultFiller: FillerFactory = idGen => ({
  id: idGen(),
  component: placeholderText(idGen),
})

function makeSplit(
  zone: Zone,
  direction: SplitDirection,
  ratio: number,
  idGen: IdGen,
  makeFiller: FillerFactory,
): Zone {
  const first: Zone =
    zone.component !== undefined
      ? { id: idGen(), component: zone.component }
      : makeFiller(idGen)
  return {
    id: zone.id,
    split: { direction, ratio },
    children: [first, makeFiller(idGen)],
  }
}

function replaceChild(zone: Zone, index: 0 | 1, replacement: Zone): Zone {
  if (!zone.children || !zone.split) return zone
  const [a, b] = zone.children
  const nextChildren: [Zone, Zone] = index === 0 ? [replacement, b] : [a, replacement]
  return { ...zone, children: nextChildren }
}

function traverseForUpdate(
  zone: Zone,
  path: string[],
  fn: (target: Zone) => Zone
): Zone {
  if (path.length === 0) return fn(zone)
  if (!zone.children || !zone.split) return zone
  const [head, ...rest] = path
  const index = Number(head)
  if (index !== 0 && index !== 1) return zone
  const childIndex = index as 0 | 1
  const child = zone.children[childIndex]
  const updatedChild = traverseForUpdate(child, rest, fn)
  return replaceChild(zone, childIndex, updatedChild)
}

export function splitZone(
  root: Zone,
  path: string[],
  direction: SplitDirection,
  ratio = 0.5,
  idGen: IdGen = defaultIdGen,
  makeFiller: FillerFactory = defaultFiller,
): Zone {
  return traverseForUpdate(root, path, target =>
    makeSplit(target, direction, ratio, idGen, makeFiller)
  )
}

/**
 * Initial split ratio for splitting the host of a baked-block-instance.
 * Returns the ratio that lets the block fill child[0] at its declared
 * `placement.{widthMm|heightMm}` and lets the remainder go to child[1].
 * For non-baked hosts (or zero/negative host dim), returns `defaultRatio`
 * (0.5) so the caller's call site stays uniform.
 *
 * The result is clamped into the schema range [0.05, 0.95] by
 * `ratioForChildSize`, so callers can hand it straight to `splitZone`.
 */
export function splitRatioForZone(
  zone: {
    w: number
    h: number
    component: ZoneComponent | undefined
  },
  direction: SplitDirection,
  defaultRatio = 0.5,
): number {
  if (zone.component === undefined || zone.component.type !== 'baked-block-instance') return defaultRatio
  const baked = zone.component
  const blockAxis = direction === 'row' ? baked.placement.widthMm : baked.placement.heightMm
  const hostAxis = direction === 'row' ? zone.w : zone.h
  if (hostAxis <= 0) return defaultRatio
  return ratioForChildSize(blockAxis, hostAxis)
}

export function resizeSplit(root: Zone, path: string[], newRatio: number): Zone {
  return traverseForUpdate(root, path, target => {
    if (!target.split) return target
    return { ...target, split: { ...target.split, ratio: newRatio } }
  })
}

export function mergeZone(
  root: Zone,
  path: string[],
  keep: 0 | 1 | 'none' = 0,
  idGen: IdGen = defaultIdGen,
  makeFiller: FillerFactory = defaultFiller,
): Zone {
  return traverseForUpdate(root, path, target => {
    if (!target.children || !target.split) return target
    if (keep === 'none') {
      return { ...makeFiller(idGen), id: target.id }
    }
    const kept = target.children[keep]
    const merged: Zone = {
      id: target.id,
      ...(kept.split && kept.children
        ? { split: kept.split, children: kept.children }
        : {}),
      ...(kept.component ? { component: kept.component } : {}),
    }
    // A kept bare leaf (no split, no component) is only legal on the template
    // side; normalize it through the filler so block layouts never carry one.
    if (!merged.split && !merged.component) {
      return { ...makeFiller(idGen), id: target.id }
    }
    return merged
  })
}

/**
 * Heals block layouts that were saved while mergeZone's keep='none' still
 * produced template-shaped bare leaves (Gitea issue #6). A leaf with neither
 * `component` nor `split` gets the canonical empty-text placeholder; id,
 * style and borders are preserved. Returns the input reference unchanged
 * when nothing needs healing, so history baselines stay reference-stable.
 * Block-side only — template layouts legitimately contain bare leaves.
 */
export function normalizeBlockLayout(zone: Zone, idGen: IdGen = defaultIdGen): Zone {
  if (zone.split && zone.children) {
    const [a, b] = zone.children
    const na = normalizeBlockLayout(a, idGen)
    const nb = normalizeBlockLayout(b, idGen)
    if (na === a && nb === b) return zone
    return { ...zone, children: [na, nb] }
  }
  if (zone.component) return zone
  return { ...zone, component: placeholderText(idGen) }
}

export function hasContent(zone: Zone): boolean {
  if (zone.component) return !isEmptyPlaceholder(zone.component)
  if (zone.split && zone.children) return zone.children.some(hasContent)
  return false
}

export function snapRatio(ratio: number, tolerance = 0.02): number {
  const SNAP_POINTS = [0.25, 0.33, 0.5, 0.67, 0.75]
  for (const snap of SNAP_POINTS) {
    if (Math.abs(ratio - snap) <= tolerance) return snap
  }
  return Math.max(0.05, Math.min(0.95, ratio))
}
